import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CheckCircle,
  Calendar,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  Lock,
} from 'lucide-react';
import { Card, Button } from '@azyntor-labs/nexus-ui';
import { enrollmentLinksApi } from '@/api/endpoints';
import { useExamAuth } from '@/hooks/useExamAuth';
import { toast } from '@/utils/toast';
import { format } from 'date-fns';
import { formatExamDateTime } from '@/utils';

export const EnrollViaMagicLink = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useExamAuth();
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});

  // Capture UTM parameters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};

    if (params.get('utm_source')) utm.utm_source = params.get('utm_source')!;
    if (params.get('utm_medium')) utm.utm_medium = params.get('utm_medium')!;
    if (params.get('utm_campaign')) utm.utm_campaign = params.get('utm_campaign')!;
    if (params.get('utm_term')) utm.utm_term = params.get('utm_term')!;
    if (params.get('utm_content')) utm.utm_content = params.get('utm_content')!;

    setUtmParams(utm);
  }, []);

  // Fetch link preview
  const { data: previewResponse, isLoading, error } = useQuery({
    queryKey: ['link-preview', token],
    queryFn: () => token ? enrollmentLinksApi.getPreview(token) : Promise.resolve(null),
    enabled: !!token,
    retry: false,
  });

  const linkData = previewResponse?.data?.data;

  // Confirm enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: () => {
      if (!token) throw new Error('Token is required');
      return enrollmentLinksApi.confirmEnrollment({
        token,
        ...utmParams,
      });
    },
    onSuccess: () => {
      toast.success('Successfully enrolled in exam!');
      // Redirect to student dashboard or exam details
      setTimeout(() => {
        navigate('/student/dashboard');
      }, 2000);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to enroll';
      toast.error(message);
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d12] flex items-center justify-center p-6">
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 mx-auto text-teal-400 animate-spin mb-4" />
          <p className="text-gray-400">Validating enrollment link...</p>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !linkData) {
    return (
      <div className="min-h-screen bg-[#0d0d12] flex items-center justify-center p-6">
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-gray-400 mb-6">
            This enrollment link is invalid, expired, or has reached its maximum uses.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  // Not authenticated - prompt login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0d0d12] flex items-center justify-center p-6">
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <Lock className="w-16 h-16 mx-auto text-teal-400 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
            <p className="text-gray-400">
              Please login to enroll in this exam via magic link
            </p>
          </div>

          {/* Exam Preview */}
          <div className="bg-[#0d0d12]/50 border border-white/[0.06] rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-white mb-2">{linkData.exam_title}</h3>
            <div className="space-y-1 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Duration: {linkData.duration} minutes
              </div>
              {linkData.start_time && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Starts: {formatExamDateTime(linkData.start_time)}
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={() => navigate(`/login?returnUrl=/enroll/${token}${window.location.search}`)}
            className="w-full bg-teal-500 hover:bg-teal-600 text-white"
            leftIcon={<ExternalLink className="w-4 h-4" />}
          >
            Login to Enroll
          </Button>

          <p className="text-center text-sm text-gray-500 mt-4">
            Don't have an account?{' '}
            <button
              onClick={() => navigate(`/register?returnUrl=/enroll/${token}${window.location.search}`)}
              className="text-teal-400 hover:text-teal-300"
            >
              Register here
            </button>
          </p>
        </Card>
      </div>
    );
  }

  // Authenticated - show enrollment confirmation
  return (
    <div className="min-h-screen bg-[#0d0d12] flex items-center justify-center p-6">
      <Card className="bg-[#12121a]/80 border-white/[0.06] p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-teal-500/10 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-teal-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Confirm Your Enrollment</h2>
          <p className="text-gray-400">
            You're about to enroll in the following exam
          </p>
        </div>

        {/* Exam Details Card */}
        <div className="bg-[#0d0d12] border border-white/[0.06] rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">{linkData.exam_title}</h3>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-400">Duration:</span>
              <p className="text-white font-medium">{linkData.duration} minutes</p>
            </div>
            {linkData.start_time && (
              <div>
                <span className="text-gray-400">Start Date:</span>
                <p className="text-white font-medium">
                  {formatExamDateTime(linkData.start_time)}
                </p>
              </div>
            )}
            {linkData.end_time && (
              <div>
                <span className="text-gray-400">End Date:</span>
                <p className="text-white font-medium">
                  {format(new Date(linkData.end_time), 'MMM dd, yyyy HH:mm')}
                </p>
              </div>
            )}
            {linkData.max_uses && (
              <div>
                <span className="text-gray-400">Remaining Slots:</span>
                <p className="text-white font-medium">
                  {linkData.max_uses - linkData.current_uses} / {linkData.max_uses}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* User Info */}
        <div className="bg-teal-500/5 border border-teal-500/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-400">
            Enrolling as: <span className="text-white font-medium">{user?.email}</span>
          </p>
        </div>

        {/* UTM Campaign Info (if present) */}
        {Object.keys(utmParams).length > 0 && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 mb-6">
            <p className="text-xs text-purple-400 font-medium mb-2">Campaign Tracking:</p>
            <div className="text-xs text-gray-400 space-y-1">
              {Object.entries(utmParams).map(([key, value]) => (
                <div key={key}>
                  {key}: <span className="text-gray-300">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex-1 text-gray-400"
            disabled={enrollMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => enrollMutation.mutate()}
            disabled={enrollMutation.isPending}
            leftIcon={enrollMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
          >
            {enrollMutation.isPending ? 'Enrolling...' : 'Confirm Enrollment'}
          </Button>
        </div>
      </Card>
    </div>
  );
};
